import { c as _c } from "react/compiler-runtime";
import { Details } from '@cc-shopping/ui/primitives/Details';
import { palette } from '@cc-shopping/ui/theme';
import { Box, Typography } from '@mui/material';
import { useId } from 'react';
import { renderStructuredContent } from '../../renderContext';
import { ACCORDION_BORDER_RADIUS } from './constants';
import type { CreditCardsAccordionItemProps } from './types';
const AccordionDetailsSemanticMapper = {
  FAQ: {
    itemScope: true,
    itemProp: 'mainEntity',
    itemType: 'https://schema.org/Question'
  }
};
const AccordionItemSemanticMapper = {
  FAQ: {
    itemScope: true,
    itemProp: 'acceptedAnswer',
    itemType: 'https://schema.org/Answer'
  }
};
export const CreditCardsAccordionItem = t0 => {
  const $ = _c(34);
  const {
    props
  } = t0;
  const {
    accordionType,
    children,
    label
  } = props;
  const idSuffix = useId();
  let t1;
  if ($[0] !== accordionType) {
    t1 = accordionType === "FAQ" ? AccordionDetailsSemanticMapper[accordionType] : {};
    $[0] = accordionType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const detailsProps = t1;
  let t2;
  if ($[2] !== accordionType) {
    t2 = accordionType === "FAQ" ? AccordionItemSemanticMapper[accordionType] : {};
    $[2] = accordionType;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const itemContentProps = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: "flex",
      alignItems: "center",
      gap: 2
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== label) {
    t5 = <Box component="span" sx={t4}><Typography variant="smallHeadline" component="span" role="heading" aria-level={3}>{label}</Typography></Box>;
    $[6] = label;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="mask-icon mask-icon-expand-more expand-icon" style={{
      width: "24px",
      height: "24px",
      backgroundColor: palette.common.black
    }} />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <Box component="span" sx={t3}>{t5}{t6}</Box>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `cc-accordion-content-${idSuffix}`;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      paddingY: "12px",
      paddingX: {
        xs: 2,
        sm: 3
      }
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8) {
    t10 = {
      "aria-controls": t8,
      "data-testid": "AccordionPanel",
      itemProp: "name",
      sx: t9
    };
    $[12] = t8;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== accordionType) {
    t11 = accordionType === "FAQ" && {
      open: true
    };
    $[14] = accordionType;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      "&:not(:last-of-type)": {
        borderBottom: "1px solid",
        borderColor: "neutral.lighter"
      },
      "&> summary:hover": {
        backgroundColor: "blues.lightest",
        outline: "2px solid",
        outlineColor: "blues.light"
      },
      "&:first-of-type": {
        "&> summary:hover": {
          borderTopLeftRadius: ACCORDION_BORDER_RADIUS,
          borderTopRightRadius: ACCORDION_BORDER_RADIUS
        }
      },
      "&:last-of-type": {
        "&> summary:hover": {
          borderBottomLeftRadius: ACCORDION_BORDER_RADIUS,
          borderBottomRightRadius: ACCORDION_BORDER_RADIUS
        }
      },
      "&[open]": {
        "&> summary:hover": {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0
        }
      }
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== detailsProps || $[18] !== t11) {
    t13 = {
      ...t11,
      ...detailsProps,
      sx: t12
    };
    $[17] = detailsProps;
    $[18] = t11;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = `cc-accordion-content-${idSuffix}`;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      p: 3
    };
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== children) {
    t16 = renderStructuredContent(children);
    $[21] = children;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t16) {
    t17 = <Box itemProp="text">{t16}</Box>;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== itemContentProps || $[26] !== t14 || $[27] !== t17) {
    t18 = <Box id={t14} {...itemContentProps} sx={t15}>{t17}</Box>;
    $[25] = itemContentProps;
    $[26] = t14;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t10 || $[30] !== t13 || $[31] !== t18 || $[32] !== t7) {
    t19 = <Details variant="simple" SummaryComponent={t7} summaryComponentProps={t10} detailsProps={t13}>{t18}</Details>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t18;
    $[32] = t7;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
};